import React from "react"

import { InlineAttachment as InlineAttachmentComponent } from "@tenforce/product-fields"

import { IInlineAttachmentField } from "../.."
import { GlobalState } from "../../../../store"
import { useAppDispatch, useAppSelector } from "../../../../hooks"
import HTMLAndInternalLinkParser from "../common/HTMLAndInternalLinkParser"
import { addErrorNotification } from "../../../notifications/actions"
import {
    addAttachment,
    addCreatorToAttachment,
    removeAttachment,
    updateAttachment,
} from "./thunks"
import { Exceptions, Labels } from "../../../../util/Translations"
import { formatString } from "../../../../util/Strings"

export const {
    AnErrorOccurredWhileLoadingImageFor = 'An error occurred while loading the image ["{0}"] for ["{1}"].',
} = Exceptions
export interface OwnProps {
    field: IInlineAttachmentField
}

type Props = OwnProps

const InlineAttachment = ({ field }: Props): JSX.Element => {
    const dispatch = useAppDispatch()

    const { isLoading } = useAppSelector((state: GlobalState) => state.fields)
    const { loadingResources } = useAppSelector(
        (state: GlobalState) => state.resources,
    )
    const { Unknown } = Labels

    const {
        odataType: _odataType,
        value: attachments,
        isReadOnly,
        isDisabled,
        isRequired,
        ...fieldProps
    } = field

    const isFieldLoading = isLoading || loadingResources.length > 0

    const addCreatorToAttachmentByIndex = React.useCallback(
        async (index: number): Promise<void> => {
            if (!attachments[index] || attachments[index].creator) {
                return
            }
            dispatch(
                addCreatorToAttachment({
                    field,
                    attachment: attachments[index],
                    unknownCreator: Unknown,
                }),
            )
        },
        [dispatch, attachments, field],
    )

    const onFilesAdded = React.useCallback(
        async (files: File[]): Promise<void> => {
            dispatch(addAttachment({ files, field }))
        },
        [addAttachment, dispatch, field],
    )

    const onUpdateAttachmentDescription = React.useCallback(
        async (id: number, value: string): Promise<void> => {
            dispatch(
                updateAttachment({
                    field,
                    attachment: attachments.find(
                        (attachment) => attachment.id === id,
                    ),
                    newValue: { Description: value },
                }),
            )
        },
        [dispatch, field, attachments, updateAttachment],
    )

    const onDeleteAttachment = React.useCallback(
        async (id: number): Promise<void> => {
            dispatch(
                removeAttachment({
                    field,
                    attachment: attachments.find(
                        (attachment) => attachment.id === id,
                    ),
                }),
            )
        },
        [dispatch, field, attachments, updateAttachment],
    )

    const onImageError = React.useCallback(
        async (event: React.SyntheticEvent): Promise<void> => {
            const image = event.target as HTMLImageElement
            const alt = image.getAttribute("alt")
            const href = image.getAttribute("href")
            const errorMessage = formatString(
                AnErrorOccurredWhileLoadingImageFor,
                alt || href || image?.toString(),
                field.title,
            )
            console.error(errorMessage)

            dispatch(addErrorNotification(errorMessage))
        },
        [
            dispatch,
            addErrorNotification,
            field.title,
            AnErrorOccurredWhileLoadingImageFor,
        ],
    )

    return (
        <InlineAttachmentComponent
            {...fieldProps}
            attachments={attachments}
            fieldName={"inline-attachment"}
            isDisabled={isDisabled}
            isLoading={isFieldLoading}
            isReadOnly={isReadOnly}
            isRequired={isRequired}
            onFilesAdded={onFilesAdded}
            description={
                <HTMLAndInternalLinkParser
                    content={(field?.description as string) ?? ""}
                />
            }
            handlers={{
                onUpdateDescription: onUpdateAttachmentDescription,
                onDelete: onDeleteAttachment,
                onImageError: onImageError,
            }}
            lightboxHandlers={{
                onShowLightbox: addCreatorToAttachmentByIndex,
                onChangeAttachmentInLightbox: addCreatorToAttachmentByIndex,
            }}
        />
    )
}

export default InlineAttachment
